import { useState, useEffect, SyntheticEvent } from "react";

import { Autocomplete, TextField, AutocompleteInputChangeReason } from "@mui/material";

interface FontSizeSelectProps {
  fontSize: string | null;
  fontSizeOptions?: string[];
  style?: React.CSSProperties | {} | undefined;
  onInputChange: (fontSize: string, hasCustomValue?: boolean) => void;
  onClose?: (reason: string) => void;
}

const fontSizeOptions: Array<string> = [
  "8", "9", "10", "11", "12",
  "14", "15", "18", "24", "30",
  "36", "48", "60", "72", "96",
];


export default function FontSizeSelect(props: FontSizeSelectProps): JSX.Element {
  const [inputValue, setInputValue] = useState<string>("");
  const [keyPressedInInput, setKeyPressedInInput] = useState<boolean>(false);
  const [cancelInput, setCancelInput] = useState<boolean>(false);

  useEffect(() => {
    if (props.fontSize) setInputValue(props.fontSize);
  }, [props.fontSize]);

  return (
    <Autocomplete
      sx={props.style && props.style}
      freeSolo
      value={props.fontSize || ""}
      inputValue={inputValue}
      disableClearable
      options={fontSizeOptions}
      renderInput={params => <TextField autoComplete="off" {...params} />}
      filterOptions={(x) => x}
      /**
       * Handles input changes for Autocomplete component
       * Font selector needs to distinguish between:
       *  "input" (user input event changes)
       *  "reset" (programatic event changes)
       *
       * @param {SyntheticEvent<Element, Event>} event - The event object
       * @param {string} newValue - The new value of the input field
       * @param {AutocompleteInputChangeReason} reason - The reason for the input change
       */
      onInputChange={(
        event: SyntheticEvent<Element, Event>,
        newValue: string,
        reason: AutocompleteInputChangeReason): void => {
          if (reason === "selectOption") {
            setKeyPressedInInput(true);
            setInputValue(newValue);
            props.onInputChange(newValue);
          }

          if (reason === "reset") {
            // The if/else is necessary. What the user types in the input field is
            // different than "newValue" selected by "click" or "enter".
            if (keyPressedInInput) {
              if (inputValue.length === 0 || cancelInput) {
                props.onInputChange(newValue);
                setKeyPressedInInput(false);
                return;
              }
              props.onInputChange(inputValue, true);
              setKeyPressedInInput(false);
            } else {
              props.onInputChange(newValue);
            }
            setCancelInput(false);
          }
      }}
      onClose={(event: React.SyntheticEvent, reason: string) => {
        if ((reason === "blur" || reason === "escape") && props.fontSize) {
          setInputValue(props.fontSize || "");

          // Allows the onInputChange to distinguish between a user selecting a value
          // or a user typing/erasing a value and then clicking away from the input field
          setCancelInput(true);
          if (props.onClose) props.onClose(reason);
        }
      }}
    />
  )
}